import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FolderKanban, Plus, Trash2, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, formatDate } from "@/lib/utils2";

const PROJECT_TEMPLATES = [
  {
    name: "Spring Planting",
    description: "Pre-season prep — tractors, planters, sprayers all serviced and ready before the field gets busy.",
    color: "#22c55e",
    emoji: "🌱",
  },
  {
    name: "Pre-Harvest Service",
    description: "Combine, headers, grain carts — full once-over before harvest hits.",
    color: "#eab308",
    emoji: "🌾",
  },
  {
    name: "Winterize",
    description: "Cold-weather prep: fuel stabilizer, batteries pulled, fluids topped, covers on.",
    color: "#3b82f6",
    emoji: "❄️",
  },
  {
    name: "Saturday Project List",
    description: "Bite-sized weekend tasks across the garage and yard.",
    color: "#ea580c",
    emoji: "🔧",
  },
  {
    name: "Pre-Season Yard Prep",
    description: "Mowers, trimmers, blowers — sharpen blades, fresh fluids, tune up.",
    color: "#10b981",
    emoji: "🌿",
  },
  {
    name: "Annual Service Roundup",
    description: "Year-end inspections, oil changes, DOT items, and registration paperwork.",
    color: "#a855f7",
    emoji: "📅",
  },
  {
    name: "Pre-Trip Inspection",
    description: "Whole-rig check before a road trip, hunting trip, or family vacation.",
    color: "#06b6d4",
    emoji: "🛻",
  },
  {
    name: "Post-Storm Walkaround",
    description: "After heavy weather: roof seals, sump pump, generator, exterior equipment.",
    color: "#f43f5e",
    emoji: "⚡",
  },
];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", color: "#ea580c" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data.items);
      // Load records per project
      const recs = {};
      for (const p of data.items) {
        const { data: r } = await api.get("/maintenance", { params: { project_id: p.project_id } });
        recs[p.project_id] = r.items;
      }
      setRecords(recs);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      setShowDialog(false);
      setForm({ name: "", description: "", color: "#ea580c" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fromTemplate = (tpl) => {
    setForm({ name: tpl.name, description: tpl.description, color: tpl.color });
    setShowDialog(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project? Tasks will stay but become unlinked.")) return;
    await api.delete(`/projects/${id}`);
    load();
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// Task bundles</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Projects</h1>
          <p className="text-zinc-400 mt-2">Group related maintenance — like "Spring Planting" or "Pre-season prep".</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="new-project-btn">
          <Plus className="w-4 h-4 mr-1" /> New project
        </Button>
      </div>

      {loading ? (
        <div className="text-zinc-500">Loading…</div>
      ) : (
        <>
          {/* Active / user-created projects — ABOVE templates for fast mobile access */}
          {projects.length > 0 ? (
            <section className="mb-10" data-testid="active-projects-section">
              <div className="flex items-center gap-3 mb-4">
                <FolderKanban className="w-4 h-4 text-orange-500" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500">// Your projects · {projects.length}</div>
                <div className="flex-1 h-px bg-zinc-900" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {projects.map((p) => {
                  const projRecords = records[p.project_id] || [];
                  return (
                    <div key={p.project_id} className="bg-zinc-900 border border-zinc-800 hover:border-orange-600 rounded-sm transition-colors" data-testid={`project-${p.project_id}`}>
                      <div className="p-5 border-b border-zinc-800">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-sm grid place-items-center" style={{ background: p.color + "33", border: `1px solid ${p.color}` }}>
                              <FolderKanban className="w-5 h-5" style={{ color: p.color }} />
                            </div>
                            <div>
                              <h3 className="font-display font-bold text-xl tracking-tight">{p.name}</h3>
                              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                                {projRecords.length} entries · started {formatDate(p.created_at)}
                              </div>
                            </div>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => del(p.project_id)} className="text-red-500 hover:bg-red-950/30">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {p.description ? <p className="text-sm text-zinc-400 mt-3">{p.description}</p> : null}
                      </div>
                      {projRecords.length > 0 ? (
                        <ul className="divide-y divide-zinc-800 max-h-60 overflow-y-auto">
                          {projRecords.slice(0, 5).map((r) => (
                            <li key={r.record_id} className="px-5 py-3 text-sm flex items-center justify-between">
                              <span className="truncate">{r.task}</span>
                              <span className="text-xs text-zinc-500 font-mono ml-2 shrink-0">{formatDate(r.performed_at)}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="p-5 bg-zinc-950/40 border-t border-zinc-800">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// Empty bundle</div>
                          <p className="text-sm text-zinc-400 mb-3">
                            Attach work by logging maintenance on a piece of equipment and selecting <span className="text-orange-500 font-semibold">{p.name}</span> as the project.
                          </p>
                          <Link to="/app/schedule">
                            <Button size="sm" className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid={`go-to-tasks-${p.project_id}`}>
                              Open tasks →
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ) : (
            <div className="mb-10 bg-zinc-900 border border-zinc-800 rounded-sm p-12 text-center">
              <FolderKanban className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
              <h3 className="font-display font-bold text-2xl mb-2">No projects yet</h3>
              <p className="text-zinc-400 mb-4">Pick a template below or start fresh.</p>
              <Button onClick={() => setShowDialog(true)} className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold">
                Create custom project
              </Button>
            </div>
          )}

          {/* Templates always below */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-4 h-4 text-orange-500" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500">// Start from a template</div>
              <div className="flex-1 h-px bg-zinc-900" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {PROJECT_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.name}
                  type="button"
                  onClick={() => fromTemplate(tpl)}
                  data-testid={`template-${tpl.name}`}
                  className="group relative p-4 bg-zinc-900 border border-zinc-800 hover:border-orange-600 hover:-translate-y-0.5 rounded-sm text-left transition-all duration-200 cursor-pointer overflow-hidden"
                  style={{ borderLeft: `3px solid ${tpl.color}` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-xl">{tpl.emoji}</div>
                    <div className="font-display font-bold text-base tracking-tight leading-tight">{tpl.name}</div>
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed line-clamp-3">{tpl.description}</p>
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-600 group-hover:text-orange-500 transition-colors">
                    Use this →
                  </div>
                </button>
              ))}
            </div>
          </section>
        </>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 rounded-sm">
          <DialogHeader>
            <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-1">// New bundle</div>
            <DialogTitle className="font-display font-black text-2xl tracking-tight">Create project</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Spring Planting 2026"
                className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm"
                data-testid="project-name-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm"
                rows={3}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Color</Label>
              <Input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm h-10 w-20"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="save-project">
                Create project
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
